/* eslint-disable react-hooks/set-state-in-effect */
/**
 * ScheduleModal Component (V2)
 *
 * Dialog für das Planen von temporären Versionen.
 * Features: DatePicker, Presets, Conflict Check, Timezone.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import DatePicker, { registerLocale } from 'react-datepicker'
import { de } from 'date-fns/locale'
import {
  CalendarCheck,
  AlertTriangle,
  Info,
  Globe,
  Loader2,
  Calendar,
} from 'lucide-react'
import 'react-datepicker/dist/react-datepicker.css'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Alert,
  AlertDescription,
  Label,
} from '@/components/v2/ui'
import { cn } from '@/lib/utils'
import {
  useCreateSchedule,
  SCHEDULE_PRESETS,
  getPresetDateRange,
  validateScheduleDateRange,
  formatScheduleDate,
} from '@/hooks/usePublishSchedule'
import { useScheduleConflict } from '@/hooks/useActiveSchedule'
import type { ScheduleEntityType, SchedulePreset } from '@/types/publishing'

// Register German locale
registerLocale('de', de)

interface ScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  entityType: ScheduleEntityType
  entityId: string
  channelId?: string
  versionId: string
  versionNumber: number
  versionSummary?: string
  currentVersionNumber?: number
}

export function ScheduleModal({
  isOpen,
  onClose,
  entityType,
  entityId,
  channelId,
  versionId,
  versionNumber,
  versionSummary,
  currentVersionNumber,
}: ScheduleModalProps) {
  const { t } = useTranslation()
  // State
  const [selectedPreset, setSelectedPreset] = useState<SchedulePreset>('tomorrow_all_day')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)

  // Queries & Mutations
  const conflictQuery = useScheduleConflict({
    entityType,
    entityId,
    enabled: isOpen,
  })

  const createMutation = useCreateSchedule(entityType, entityId, channelId)

   
  useEffect(() => {
    if (selectedPreset !== 'custom') {
      const { start, end } = getPresetDateRange(selectedPreset)
      setStartDate(start)
      setEndDate(end)
    }
  }, [selectedPreset])

  const computedValidationError = useMemo(() => {
    const result = validateScheduleDateRange(startDate, endDate)
    return result.isValid ? null : result.error || null
  }, [startDate, endDate])

  useEffect(() => {
    setValidationError(computedValidationError)
  }, [computedValidationError])

  useEffect(() => {
    if (isOpen) {
      setSelectedPreset('tomorrow_all_day')
      setValidationError(null)
    }
  }, [isOpen])
   

  // Handle submit
  const handleSubmit = () => {
    if (!startDate || !endDate) return

    const validation = validateScheduleDateRange(startDate, endDate)
    if (!validation.isValid) {
      setValidationError(validation.error || 'Ungültiger Zeitraum')
      return
    }

    createMutation.mutate({
      version_id: versionId,
      publish_start_at: startDate.toISOString(),
      publish_end_at: endDate.toISOString(),
    }, {
      onSuccess: (data) => {
        if (data.success) {
          onClose()
        }
      }
    })
  }

  // Has conflict
  const hasConflict = conflictQuery.data?.has_conflict ?? false
  const existingSchedule = conflictQuery.data?.existing_schedule

  // Can submit
  const canSubmit = useMemo(() => {
    return (
      startDate &&
      endDate &&
      !validationError &&
      !hasConflict &&
      !createMutation.isPending
    )
  }, [startDate, endDate, validationError, hasConflict, createMutation.isPending])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarCheck className="h-5 w-5" />
            {t('publishing.planVersion')}
          </DialogTitle>
          <DialogDescription>
            {t('publishing.planVersionDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Version Info */}
          <div className="p-3 bg-neutral-100 rounded-lg">
            <div className="flex items-center">
              <div className="flex-1">
                <strong className="text-neutral-900">
                  Version V{versionNumber}
                </strong>
                {versionSummary && (
                  <span className="text-neutral-500 ml-2">
                    "{versionSummary}"
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Conflict Warning */}
          {hasConflict && existingSchedule && (
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Konflikt:</strong> Es existiert bereits ein aktiver Zeitplan.
                <div className="text-sm mt-1">
                  V{existingSchedule.version?.version_number} –
                  {formatScheduleDate(existingSchedule.publish_start_at)} bis {formatScheduleDate(existingSchedule.publish_end_at)}
                </div>
                <div className="text-sm text-neutral-500 mt-2">
                  Um fortzufahren, brechen Sie zuerst den bestehenden Zeitplan ab.
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Presets */}
          <div>
            <Label className="mb-2 block">Schnellauswahl</Label>
            <div className="flex flex-wrap gap-2">
              {SCHEDULE_PRESETS.map((preset) => (
                <Button
                  key={preset.id}
                  type="button"
                  variant={selectedPreset === preset.id ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPreset(preset.id)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Date Pickers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-1 mb-2">
                <Calendar className="h-4 w-4" />
                Start
              </Label>
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => {
                  setStartDate(date)
                  setSelectedPreset('custom')
                }}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="dd.MM.yyyy HH:mm"
                locale="de"
                minDate={new Date()}
                className={cn(
                  'w-full px-3 py-2 rounded-md border',
                  'bg-white',
                  'border-neutral-300',
                  'text-neutral-900',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500'
                )}
                placeholderText={t('publishing.selectStartDate')}
                wrapperClassName="w-full"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1 mb-2">
                <Calendar className="h-4 w-4" />
                Ende
              </Label>
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => {
                  setEndDate(date)
                  setSelectedPreset('custom')
                }}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="dd.MM.yyyy HH:mm"
                locale="de"
                minDate={startDate || new Date()}
                className={cn(
                  'w-full px-3 py-2 rounded-md border',
                  'bg-white',
                  'border-neutral-300',
                  'text-neutral-900',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500'
                )}
                placeholderText={t('publishing.selectEndDate')}
                wrapperClassName="w-full"
              />
            </div>
          </div>

          {/* Validation Error */}
          {validationError && (
            <Alert variant="error">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}

          {/* Info Box */}
          <Alert variant="info">
            <Info className="h-4 w-4" />
            <AlertDescription>
              {t('publishing.afterEndRevertTo', { version: currentVersionNumber ? `V${currentVersionNumber}` : 'der aktuellen Version' })}
            </AlertDescription>
          </Alert>

          {/* Timezone Notice */}
          <div className="text-neutral-500 text-sm flex items-center gap-1">
            <Globe className="h-4 w-4" />
            Zeitzone: Europe/Berlin
          </div>

          {/* Content Block Warning */}
          {entityType === 'content_block' && (
            <Alert variant="secondary">
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>Hinweis:</strong> {t('publishing.noteChannelOutputs')}
                {t('publishing.regenerateNote')}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Planen...
              </>
            ) : (
              <>
                <CalendarCheck className="h-4 w-4 mr-2" />
                Planen
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

